/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import { environment } from '@fai/friendli-suite/shared/environments/environment';

if (environment.SENTRY_DSN) {
  Sentry.init({
    // debug: !environment.IS_PRODUCTION,
    dsn: environment.SENTRY_DSN,
    environment: environment.ENV_NAME,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      /* Captures errors on failed requests from Fetch and XHR and attaches request and response information. */
      Sentry.httpClientIntegration(),
      /* Extracts all non-native attributes from the error object and attaches them to the event as extra data */
      Sentry.extraErrorDataIntegration(),
      /* Captures all Console API calls via `captureException` or `captureMessage`. */
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
    ],

    /**
     * @section replayIntegration
     * https://docs.sentry.io/platforms/javascript/session-replay/configuration/
     */
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1,

    /**
     * @section browserTracingIntegration
     * https://docs.sentry.io/platforms/javascript/tracing/
     */
    tracesSampleRate: 0.8,
    tracePropagationTargets: [
      'localhost',
      'https://api-dev.friendli.ai',
      'https://suite.friendli.ai',
    ],

    /**
     * @section httpClientIntegration
     * https://docs.sentry.io/platforms/javascript/guides/nextjs/configuration/integrations/httpclient/
     */
    sendDefaultPii: true,

    /**
     * when error type is plain object not Error
     * https://github.com/getsentry/sentry-javascript/issues/2292
     */
    ignoreErrors: ['Non-Error exception captured'],
  });
}
